import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, Alert, AlertTitle, Stack, useTheme } from '@mui/material';
import { formatSimpleDate } from '@frontend/features/common/helper';
import { CheckCircle } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

export type HotelCancelPenaltyType = 'NIGHTS' | 'PERCENT' | 'IMPORT';

export type HotelCancelPenalty = {
  deadline?: string;
  isCalculatedDeadline: boolean;
  penaltyType: HotelCancelPenaltyType;
  currency: string;
  value: number;
};

export type HotelCancelPolicy = {
  refundable: boolean;
  gross: number;
  cancelPenalties: HotelCancelPenalty[];
  description?: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  cancelPolicy: HotelCancelPolicy;
};

export const HotelCancelPolicyModal: React.FC<Props> = ({ open, onClose, cancelPolicy }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const renderPenalty = (penalty: HotelCancelPenalty, idx: number) => {
    const dateStr = penalty.deadline
      ? formatSimpleDate(penalty.deadline)
      : 'Sin fecha límite específica';

    let valueDescription;
    switch (penalty.penaltyType) {
    case 'NIGHTS':
      valueDescription = <Typography variant='body2' color="text.secondary">{penalty.value} {t('hotels.cancelPolicyModal.penaltyNights')}</Typography>;
      break;
    case 'PERCENT':

      valueDescription = <Typography variant='body2' color="text.secondary">{penalty.value}{t('hotels.cancelPolicyModal.fromTotal')}</Typography>;
      break;
    case 'IMPORT':
      valueDescription = <Box display="flex" justifyContent="space-between" gap={1}>
        <Typography variant='body2' color="text.secondary">{t('hotels.cancelPolicyModal.fixedAmount')}</Typography>
        <Typography variant='subtitle2' color="text.primary">{penalty.value} {penalty.currency}</Typography>
      </Box>;
      break;
    default:
      valueDescription = <Typography variant='body2' color="text.secondary">{t('hotels.cancelPolicyModal.unknownPenalty')}</Typography>;
    }

    return (
      <Box display="flex" justifyContent="space-between" key={idx}>
          
        <Typography variant='body2' color="text.secondary">{dateStr}</Typography>
        
        <Typography variant='subtitle2' color="text.primary">{valueDescription}</Typography>
          
      </Box>

    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm">
      <DialogTitle>{t('hotels.cancelPolicyModal.title')}</DialogTitle>
      <DialogContent dividers>
        <Box display="flex" flexDirection="column" gap={3}>
          {cancelPolicy?.refundable &&
            <Box display="flex" gap={1} alignItems="center">
              <CheckCircle fontSize='small' color="success" />
              <Typography color="success.dark" variant="subtitle2">
                {t('hotels.cancelPolicyModal.refundableFare')}
              </Typography>
            </Box>
          }
          {cancelPolicy?.description && (
            <Box
              sx={{
                '.ck-content': {
                  ...theme.typography.body2,
                  border: 'none'
                },
                '.ck-reset_all': {
                  display: 'none',
                },
                mt: theme.spacing(2)
              }}
            >
              <CKEditor
                editor={ClassicEditor as any}
                data={cancelPolicy.description}
                disabled
                config={{
                  toolbar: [],
                }}
              />
            </Box>
          )}
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Stack direction="column" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2" color="text.primary">{t('hotels.cancelPolicyModal.limitDate')}</Typography>
            </Stack>
            <Typography variant="subtitle2" color="text.primary">{t('hotels.cancelPolicyModal.penaltyTitle')}</Typography>
          </Box>
          {
            Boolean(cancelPolicy?.cancelPenalties?.length) && (
              <>
                {cancelPolicy?.cancelPenalties?.map(renderPenalty)}
    
                <Alert severity='warning'>
                  <AlertTitle>{t('hotels.cancelPolicyModal.additionalDetails')}</AlertTitle>
                  {cancelPolicy?.cancelPenalties[0].isCalculatedDeadline ? t('hotels.cancelPolicyModal.dateCaluclatedBySystem') : t('hotels.cancelPolicyModal.dateCaluclatedByProvider')}
                </Alert>
              </>
            )
          }
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};
